import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { User } from '../types';
import { decodeJwt, JWTPayload } from '../utils/jwtUtils';
import { toNumberId } from '../utils/typeUtils';

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: () => void;
  logout: () => void;
  token: string | null;
  // New helper to allow external components to feed a token
  ingestToken: (rawToken: string | null) => void;
  // Re-run auth check (e.g. after navigation changes)
  refreshAuth: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  login: () => { },
  logout: () => { },
  token: null,
  ingestToken: () => { },
  refreshAuth: () => { },
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const performDecode = (storedToken: string) => {
    try {
      const decoded = decodeJwt<JWTPayload>(storedToken);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        console.log('Token expired');
        localStorage.removeItem('auth_token');
        setIsAuthenticated(false);
        setUser(null);
        setToken(null);
        return;
      }
      const userId = toNumberId(decoded.azureId);
      if (!userId) {
        console.error('Invalid user ID in token');
        localStorage.removeItem('auth_token');
        return;
      }
      setUser({
        id: userId,
        name: decoded.name,
        email: decoded.email,
        department: decoded.department,
        role: decoded.role as 'admin' | 'user',
        createdAt: new Date().toISOString(),
      });
      setToken(storedToken);
      setIsAuthenticated(true);
    } catch (err) {
      console.error('Invalid token:', err);
      localStorage.removeItem('auth_token');
    }
  };

  const refreshAuth = () => {
    const storedToken = localStorage.getItem('auth_token');
    if (storedToken) {
      performDecode(storedToken);
    } else {
      setIsAuthenticated(false);
      setUser(null);
      setToken(null);
    }
  };

  const ingestToken = (rawToken: string | null) => {
    if (!rawToken) return;
    localStorage.setItem('auth_token', rawToken);
    performDecode(rawToken);
  };

  // Initial bootstrap (single run) - parse query param then decode
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenFromUrl = urlParams.get('token');
    if (tokenFromUrl) {
      ingestToken(tokenFromUrl);
      // Clean URL without token param
      const newUrl = window.location.pathname + window.location.hash; // omit search
      window.history.replaceState({}, document.title, newUrl || '/');
    } else {
      refreshAuth();
    }
  }, []);

  // Redirect to Azure AD login
  const login = () => {
    window.location.href = '/auth/login';  // Use relative path for same-port deployment
  };

  // Clear token and user data
  const logout = () => {
    localStorage.removeItem('auth_token');
    setUser(null);
    setToken(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, token, ingestToken, refreshAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
